// 履歴パネルコンポーネント

import { useState } from 'react';
import { CalculationHistory } from '../types';

interface HistoryPanelProps {
  history: CalculationHistory[];
  onSelect: (item: CalculationHistory) => void;
  onClear: () => void;
  onRemove: (id: string) => void;
}

export function HistoryPanel({
  history,
  onSelect,
  onClear,
  onRemove,
}: HistoryPanelProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleClear = () => {
    if (showClearConfirm) {
      onClear();
      setShowClearConfirm(false);
    } else {
      setShowClearConfirm(true);
    }
  };

  const formatDateTime = (date: Date) => {
    return date.toLocaleString('ja-JP', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatArea = (area: number): string => {
    if (area === 0) return '0';

    // Simplified approach
    const absArea = Math.abs(area);

    if (absArea < 1000) {
      return area.toLocaleString();
    } else if (absArea < 1000000) {
      return (area / 1000).toFixed(1) + 'K';
    } else {
      return (area / 1000000).toFixed(1) + 'M';
    }
  };

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          計算履歴
        </h2>
        
        {history.length > 0 && (
          <button
            onClick={handleClear}
            className={`text-sm transition-colors duration-200 ${
              showClearConfirm 
                ? 'text-red-600 hover:text-red-700 font-semibold' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            title={showClearConfirm ? '確認のためもう一度クリック' : '履歴をクリア'}
          >
            {showClearConfirm ? '確認：全削除' : '履歴クリア'}
          </button>
        )}
      </div>

      {history.length === 0 ? (
        <div className="text-center py-8">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
          </svg>
          <p className="mt-2 text-sm text-gray-500">
            計算履歴はまだありません
          </p>
          <p className="text-xs text-gray-400 mt-1">
            計算を実行すると、ここに履歴が表示されます
          </p>
        </div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {history.map((item) => (
            <div
              key={item.id}
              className="border border-gray-200 rounded-lg hover:border-gray-300 transition-colors duration-200"
            >
              {/* ヘッダー部分 */}
              <div className="p-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className="font-semibold text-gray-900">
                        {item.input.materialName}
                      </span>
                      <span className="text-sm text-blue-600 font-semibold">
                        {item.result.totalPieces}枚
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {formatDateTime(item.timestamp)}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-1 ml-2">
                    <button
                      onClick={() => toggleExpand(item.id)}
                      className="p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                      title="詳細を表示"
                    >
                      <svg
                        className={`h-4 w-4 transition-transform duration-200 ${
                          expandedId === item.id ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    <button
                      onClick={() => onRemove(item.id)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors duration-200"
                      title="この履歴を削除"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* 基本情報 */}
                <div className="mt-2 text-sm text-gray-600">
                  <span>{item.input.pieceWidth} × {item.input.pieceHeight} mm</span>
                  <span className="mx-2">•</span>
                  <span>{item.result.orientation}</span>
                </div>
              </div>

              {/* 詳細情報（展開時） */}
              {expandedId === item.id && (
                <div className="border-t border-gray-100 p-3 bg-gray-50">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-gray-700">板材{item.result.selectedVeneer}:</div>
                      <div className="text-gray-900">
                        {item.result.veneerWidth} × {item.result.veneerHeight} mm
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-700">配置:</div>
                      <div className="text-gray-900">
                        {item.result.horizontalCount} × {item.result.verticalCount}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-700">刃幅:</div>
                      <div className="text-gray-900">{item.input.kerf} mm</div>
                    </div>
                    <div>
                      <div className="text-gray-700">余り面積:</div>
                      <div className="text-gray-900">
                        {formatArea(item.result.leftoverArea)} mm²
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => onSelect(item)}
                    className="w-full mt-3 btn-secondary text-sm"
                  >
                    この条件で再計算
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 統計情報 */}
      {history.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">統計情報</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-gray-600">計算回数</div>
              <div className="font-semibold text-gray-900">{history.length} 回</div>
            </div>
            <div>
              <div className="text-gray-600">総切り出し枚数</div>
              <div className="font-semibold text-gray-900">
                {history.reduce((sum, item) => sum + item.result.totalPieces, 0)} 枚
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}